import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Undo2, Plus, Minus, Users, CalendarClock } from "lucide-react";
import usePermissions from "@/lib/usePermissions";
import PlannerSummaryCard from "@/components/planner/PlannerSummaryCard";
import PlannerTimeline from "@/components/planner/PlannerTimeline";
import { AddProjectDialog, ManagePeopleDialog } from "@/components/planner/PlannerDialogs";
import { addMonths, fmtMonthLabel, fmtYMD, startOfMonth } from "@/lib/plannerDate";

const ZOOM_LEVELS = [3, 4, 5, 6, 8, 10, 13];

/**
 * Studio Project Planner — cross-BU multi-project Gantt hub. Built to match
 * a Claude-artifact reference the founder shared 1:1 (summary cards per BU,
 * phase-tagged draggable timeline blocks, Today marker, monthly zoom).
 *
 * Data model: a "phase block" on the timeline IS a `milestones` doc with a
 * `phase` tag — drag/resize/add/edit/delete all go through the EXISTING
 * /milestones endpoints (see server.py). This page only additionally calls
 * the /planner/* aggregation + row-action endpoints (see planner.py).
 */
export default function StudioPlanner() {
  const can = usePermissions();
  const canEdit = can("projects_edit");

  const [phasesData, setPhasesData] = useState({ phases: [], bu_groups: [], bu_colors: {} });
  const [overview, setOverview] = useState(null);
  const [board, setBoard] = useState({ projects: [] });
  const [loading, setLoading] = useState(true);

  const [asOf, setAsOf] = useState(fmtYMD(new Date()));
  const [windowMonths, setWindowMonths] = useState(9);
  const [show, setShow] = useState("all");
  const [order, setOrder] = useState("manual");
  const [zoomIdx, setZoomIdx] = useState(2);
  const [addOpen, setAddOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [lastAction, setLastAction] = useState(null);

  const windowStart = useMemo(() => startOfMonth(new Date()), []);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/planner/overview", { params: { as_of: asOf } });
      setOverview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [asOf]);

  const loadBoard = useCallback(async () => {
    try {
      const { data } = await api.get("/planner/board", { params: { show, order } });
      setBoard(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [show, order]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planner/phases");
        setPhasesData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      setLoading(false);
    })();
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const refetchAll = useCallback(async () => { await Promise.all([loadOverview(), loadBoard()]); }, [loadOverview, loadBoard]);

  const findBlock = (blockId) => {
    for (const p of board.projects) {
      const b = (p.blocks || []).find((x) => x.id === blockId);
      if (b) return b;
    }
    return null;
  };

  const handleBlockMove = async (blockId, newStart, newEnd) => {
    const prev = findBlock(blockId);
    try {
      await api.patch(`/milestones/${blockId}`, { start_date: newStart, due_date: newEnd });
      if (prev) {
        setLastAction({
          label: "block move", run: async () => {
            await api.patch(`/milestones/${blockId}`, { start_date: prev.start_date, due_date: prev.due_date });
            setLastAction(null);
            await refetchAll();
          },
        });
      }
      await refetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleBlockAdd = async (projectId, payload) => {
    try {
      await api.post("/milestones", { project_id: projectId, order: 0, status: "planned", ...payload });
      toast.success("Block added");
      await refetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleBlockEdit = async (blockId, patch) => {
    try {
      await api.patch(`/milestones/${blockId}`, patch);
      await refetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleBlockDelete = async (blockId) => {
    try {
      await api.delete(`/milestones/${blockId}`);
      toast.success("Block deleted");
      await refetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleStageToggle = async (projectId, newStage) => {
    const proj = board.projects.find((p) => p.id === projectId);
    const prevStage = proj?.planner_stage || "active";
    try {
      await api.patch(`/planner/projects/${projectId}/stage`, { planner_stage: newStage });
      setLastAction({
        label: "stage change", run: async () => {
          await api.patch(`/planner/projects/${projectId}/stage`, { planner_stage: prevStage });
          setLastAction(null);
          await refetchAll();
        },
      });
      await refetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleReorder = async (orderArray) => {
    try {
      await api.patch("/planner/projects/order", { order: orderArray });
      await loadBoard();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAddProject = async (payload) => {
    try {
      await api.post("/planner/projects", payload);
      toast.success("Project added to the board");
      await refetchAll();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const handleSetAllocation = async (projectId, userId, pct) => {
    try {
      await api.patch(`/planner/projects/${projectId}/team/${userId}/allocation`, { allocation_pct: pct });
      await refetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="p-8 text-sm text-slate-400" data-testid="planner-loading">Loading planner…</div>;

  const rangeLabel = `${fmtMonthLabel(windowStart, true)} to ${fmtMonthLabel(addMonths(windowStart, windowMonths - 1), true)}`;

  return (
    <div className="space-y-4" data-testid="studio-planner-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest">
            <span style={{ color: "#F54800" }}>Analogy Design</span> <span className="text-[#2F5EFF]">Studio Planner</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-0.5">Project planner</h1>
          <div className="text-xs text-slate-400 mt-0.5">{rangeLabel}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Synced</span>
          {lastAction && (
            <Button variant="outline" size="sm" onClick={lastAction.run} className="rounded-sm" data-testid="planner-undo-btn">
              <Undo2 size={13} className="mr-1.5" /> Undo {lastAction.label}
            </Button>
          )}
          <div className="flex items-center gap-1 border border-slate-200 rounded-sm px-1.5 py-1">
            <button onClick={() => setWindowMonths((m) => Math.max(3, m - 1))} className="text-slate-400 hover:text-slate-700" data-testid="planner-window-minus"><Minus size={13} /></button>
            <span className="text-xs font-medium text-slate-600 w-16 text-center">{windowMonths} months</span>
            <button onClick={() => setWindowMonths((m) => Math.min(18, m + 1))} className="text-slate-400 hover:text-slate-700" data-testid="planner-window-plus"><Plus size={13} /></button>
          </div>
          {canEdit && (
            <Button size="sm" onClick={() => setAddOpen(true)} className="rounded-sm bg-[#F54800] hover:bg-[#D93D00] text-white" data-testid="planner-add-project-btn">
              <Plus size={14} className="mr-1.5" /> Add project
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards + as-of controls */}
      <div className="card-flat p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <CalendarClock size={14} className="text-slate-400" />
            <span className="text-xs text-slate-500">As of</span>
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-7 w-36 text-xs rounded-sm" data-testid="planner-as-of-input" />
            <Button variant="outline" size="sm" className="h-7 rounded-sm text-xs" onClick={() => setAsOf(fmtYMD(new Date()))} data-testid="planner-today-btn">Today</Button>
            {canEdit && (
              <Button variant="outline" size="sm" className="h-7 rounded-sm text-xs" onClick={() => setPeopleOpen(true)} data-testid="planner-manage-people-btn">
                <Users size={12} className="mr-1.5" /> Manage people
              </Button>
            )}
          </div>
          <div className="text-[11px] text-slate-400 max-w-md text-right">
            Big number: active projects each team still has work on (waitlist excluded). Running: work happening on this date. Bars: most running at once each month.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(overview?.cards || []).map((c) => <PlannerSummaryCard key={c.bu} card={c} />)}
        </div>

        {/* Phase legend */}
        <div className="flex items-center gap-4 flex-wrap mt-4 pt-3 border-t border-slate-100" data-testid="planner-legend">
          {phasesData.phases.map((p) => (
            <span key={p.key} className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: p.color }} /> {p.label}
            </span>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-end gap-4 flex-wrap text-xs">
        <SegmentedControl label="Show" value={show} onChange={setShow} options={[["all", "All"], ["active", "Active"], ["waitlist", "Waitlist"]]} testPrefix="planner-show" />
        <SegmentedControl label="Order" value={order} onChange={setOrder} options={[["manual", "Manual"], ["by_bu", "By BU"], ["by_start_date", "By start date"]]} testPrefix="planner-order" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-400">Zoom</span>
          <button onClick={() => setZoomIdx((i) => Math.max(0, i - 1))} className="w-6 h-6 rounded-sm border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="planner-zoom-minus">−</button>
          <button onClick={() => setZoomIdx((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))} className="w-6 h-6 rounded-sm border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="planner-zoom-plus">+</button>
        </div>
      </div>

      <PlannerTimeline
        projects={board.projects}
        phases={phasesData.phases}
        buColors={phasesData.bu_colors}
        orderMode={order}
        pxPerDay={ZOOM_LEVELS[zoomIdx]}
        windowStart={windowStart}
        windowMonths={windowMonths}
        canEdit={canEdit}
        onBlockMove={handleBlockMove}
        onBlockAdd={handleBlockAdd}
        onBlockEdit={handleBlockEdit}
        onBlockDelete={handleBlockDelete}
        onStageToggle={handleStageToggle}
        onReorder={handleReorder}
      />

      <AddProjectDialog open={addOpen} onClose={() => setAddOpen(false)} buGroups={phasesData.bu_groups} onCreate={handleAddProject} />
      <ManagePeopleDialog open={peopleOpen} onClose={() => setPeopleOpen(false)} projects={board.projects} onSetAllocation={handleSetAllocation} />
    </div>
  );
}

function SegmentedControl({ label, value, onChange, options, testPrefix }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-slate-400">{label}</span>
      <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden">
        {options.map(([v, l]) => (
          <button
            key={v}
            onClick={() => onChange(v)}
            className={`px-2 py-1 text-[11px] font-medium transition-colors ${value === v ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
            data-testid={`${testPrefix}-${v}`}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}
